import { useState } from 'react';

const NOTICE_HIDE_DATE_KEY = 'castar_post_login_notice_hide_date';
const NOTICE_PENDING_KEY = 'castar_post_login_notice_pending';

function getTodayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function markPostLoginNoticePending() {
  try {
    sessionStorage.setItem(NOTICE_PENDING_KEY, 'true');
  } catch {}
}

export function shouldShowPostLoginNotice() {
  try {
    const isPending = sessionStorage.getItem(NOTICE_PENDING_KEY) === 'true';
    const hiddenDate = localStorage.getItem(NOTICE_HIDE_DATE_KEY);
    return isPending && hiddenDate !== getTodayKey();
  } catch {
    return false;
  }
}

export function clearPostLoginNoticePending() {
  try {
    sessionStorage.removeItem(NOTICE_PENDING_KEY);
  } catch {}
}

export function hidePostLoginNoticeToday() {
  try {
    localStorage.setItem(NOTICE_HIDE_DATE_KEY, getTodayKey());
    sessionStorage.removeItem(NOTICE_PENDING_KEY);
  } catch {}
}

const DECORATIONS = [
  { src: '/assets/images/vector1.png', style: { top: '0', left: '0' } },
  { src: '/assets/images/Vector2.png', style: { top: '0', right: '0' } },
  { src: '/assets/images/Vector3.png', style: { bottom: '120px', left: '0' } },
  { src: '/assets/images/Vector4.png', style: { bottom: '110px', right: '0' } },
];

const isMobileView = () => window.innerWidth <= 480;

function getTextStyles() {
  const mobile = isMobileView();
  return {
    title: {
      alignSelf: 'stretch',
      color: '#000',
      fontFamily: 'Pretendard, sans-serif',
      fontSize: mobile ? '16px' : '20px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: mobile ? '22px' : '28px',
      letterSpacing: '-0.8px',
      whiteSpace: 'pre-line',
    },
    body: {
      alignSelf: 'stretch',
      color: '#000',
      fontFamily: 'Pretendard, sans-serif',
      fontSize: mobile ? '13px' : '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: mobile ? '18px' : '22.4px',
      letterSpacing: '-0.64px',
      whiteSpace: 'pre-line',
    },
    signature: {
      alignSelf: 'stretch',
      color: '#000',
      fontFamily: 'Pretendard, sans-serif',
      fontSize: mobile ? '13px' : '16px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: mobile ? '18px' : '22.4px',
      letterSpacing: '-0.64px',
      whiteSpace: 'pre-line',
    },
  };
}

function ActionLinkButton({ label, background, color, onClick }) {
  const mobile = isMobileView();
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        width: mobile ? '84px' : '100px',
        padding: mobile ? '4px' : '5px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        borderRadius: '5px',
        background,
        border: 'none',
        cursor: 'pointer',
        color,
        fontFamily: 'Pretendard, sans-serif',
        fontSize: mobile ? '12px' : '14px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '20px',
        letterSpacing: '-0.56px',
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      {label}
    </button>
  );
}

function FooterActionButton({ label, onClick }) {
  const [hovered, setHovered] = useState(false);
  const mobile = isMobileView();

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: '1 0 0',
        background: hovered ? 'rgba(119, 119, 119, 0.08)' : 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#777',
        textAlign: 'center',
        fontFamily: 'Pretendard, sans-serif',
        fontSize: mobile ? '13px' : '16px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: mobile ? '18px' : '22.4px',
        letterSpacing: '-0.64px',
        padding: mobile ? '6px 0' : '8px 0',
        margin: 0,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        outline: 'none',
        boxShadow: 'none',
        borderRadius: '6px',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }}
    >
      {label}
    </button>
  );
}

export default function PostLoginNoticeModal({ onClose, onHideToday }) {
  const mobile = isMobileView();
  const textStyles = getTextStyles();

  const handleEmailInquiry = async () => {
    const gmailUrl = 'https://mail.google.com/mail/?view=cm&fs=1&to=castar.korea@gmail.com';
    const openedWindow = window.open(gmailUrl, '_blank', 'noopener,noreferrer');
    if (openedWindow) return;

    try {
      await navigator.clipboard.writeText('castar.korea@gmail.com');
      window.alert('이메일 주소가 복사되었습니다: castar.korea@gmail.com');
    } catch {
      window.prompt('이메일 주소를 복사해주세요.', 'castar.korea@gmail.com');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.58)',
        overflowY: 'auto',
        padding: '24px 0',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(450px, 92vw)',
          maxHeight: 'calc(100dvh - 48px)',
          borderRadius: '10px',
          background: '#FFF',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          margin: 'auto',
        }}
      >
        {DECORATIONS.map(({ src, style }) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              pointerEvents: 'none',
              zIndex: 0,
              opacity: 0.28,
              ...style,
            }}
          />
        ))}
        <div style={{ padding: mobile ? '24px 20px 16px' : '36px 32px 28px', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto', position: 'relative', zIndex: 1 }}>
          <div style={{ ...textStyles.title, marginBottom: mobile ? '24px' : '40px' }}>안녕하세요, Castar 팀입니다.</div>

          <div style={{ ...textStyles.body, marginBottom: mobile ? '16px' : '25px' }}>
            {`Castar가 서비스를 정식 오픈했습니다!  🎉
현재 정식적인 운영 중이며, 사용 중 불편 사항이나 오류가 
발생할 수 있습니다.

서비스 이용 중 발생한 문제나 개선이 필요한 부분은 아래 채널로 언제든지 연락 부탁드립니다.`}
          </div>

          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: mobile ? '16px' : '28px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <ActionLinkButton
              label="카카오톡 채널"
              background="#FFE100"
              color="#000"
              onClick={() => window.open('https://pf.kakao.com/_xanYxdX', '_blank', 'noopener,noreferrer')}
            />
            <ActionLinkButton
              label="이메일 문의"
              background="#000"
              color="#FFF"
              onClick={handleEmailInquiry}
            />
            </div>
          </div>

          <div style={{ ...textStyles.body, marginBottom: '8px' }}>
            {`전달해주시는 모든 의견은 적극 검토되며, Castar 팀은 보다 
안정적이고 신뢰할 수 있는 캐스팅 환경을 제공할 수 있도록 
지속적으로 노력해 나가겠습니다.

감사합니다.`}
          </div>

          <div style={textStyles.signature}>Castar 팀 드림</div>
        </div>

        <div
          style={{
            display: 'flex',
            paddingTop: '10px',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            borderTop: '1px solid #EAEAEA',
            marginLeft: mobile ? '16px' : '24px',
            marginRight: mobile ? '16px' : '24px',
            paddingBottom: mobile ? '12px' : '18px',
            position: 'relative',
            zIndex: 1,
            flexShrink: 0,
          }}
        >
          <FooterActionButton label="오늘 보지 않기" onClick={onHideToday} />
          <FooterActionButton label="닫기" onClick={onClose} />
        </div>
      </div>
    </div>
  );
}
